import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import types from '../../mutation-types';
import ContactAPI from '../../../api/contacts';
import AccountActionsAPI from '../../../api/accountActions';
import AnalyticsHelper from '../../../helper/AnalyticsHelper';
import { CONTACTS_EVENTS } from '../../../helper/AnalyticsHelper/events';

const buildContactFormData = contactParams => {
  const formData = new FormData();
  const { additional_attributes = {}, ...contactProperties } = contactParams;
  Object.keys(contactProperties).forEach(key => {
    if (contactProperties[key]) {
      formData.append(key, contactProperties[key]);
    }
  });
  const {
    social_profiles,
    ...additionalAttributesProperties
  } = additional_attributes;
  Object.keys(additionalAttributesProperties).forEach(key => {
    formData.append(
      `additional_attributes[${key}]`,
      additionalAttributesProperties[key]
    );
  });
  Object.keys(social_profiles).forEach(key => {
    formData.append(
      `additional_attributes[social_profiles][${key}]`,
      social_profiles[key]
    );
  });
  return formData;
};

export const raiseContactCreateErrors = error => {
  if (error.response?.status === 422) {
    throw new DuplicateContactException(error.response.data.attributes);
  } else if (error.response?.data?.message) {
    throw new ExceptionWithMessage(error.response.data.message);
  } else {
    throw new Error(error);
  }
};

export const actions = {
  search: async ({ commit }, { search, page, sortAttr, label,state_id,status_id, filters }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.search(search, page, sortAttr, label,state_id,status_id,filters);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  get: async ({ commit }, { page = 1, sortAttr, label } = {}) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.get(page, sortAttr, label);
      commit(types.CLEAR_CONTACTS);
      commit(types.SET_CONTACTS, payload);
      commit(types.SET_CONTACT_META, meta);
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, { id }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await ContactAPI.show(id);
      commit(types.SET_CONTACT_ITEM, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, {
        isFetchingItem: false,
      });
    }
  },

  update: async ({ commit }, { id, isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      const response = await ContactAPI.update(
        id,
        isFormData ? buildContactFormData(contactParams) : contactParams
      );
      commit(types.EDIT_CONTACT, response.data.payload);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
      if (error.response?.status === 422) {
        throw new ExceptionWithMessage(
          error.response.data.errors?.[0] ||
          error.response.data.message
        );
      } else {
        throw new Error(error);
      }
    }
  },

  create: async ({ commit }, { isFormData = false, ...contactParams }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response = await ContactAPI.create(
        isFormData ? buildContactFormData(contactParams) : contactParams
      );
      // console.log("new contact is created", response.data.payload.contact.id);
      console.log("new contact is created", response);
      if(response.data.success == false && response.data.message == "already exist"){
        
        return response.data;
      }
      AnalyticsHelper.track(CONTACTS_EVENTS.CREATE_CONTACT);
      commit(types.SET_CONTACT_ITEM, response.data.payload.contact);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response.data.payload.contact;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return raiseContactCreateErrors(error);
    }
    finally{
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    }
  },

  import: async ({ commit }, {file, inboxId, agentList, macroId, label_list, boardId, statusId}) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
    try {
      const response=await ContactAPI.importContacts(file, inboxId, agentList, macroId, label_list, boardId, statusId);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return response.data;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message); 
      }
    }
  },

  export: async ({ commit }, { q, filters }) => {
    try {
      await ContactAPI.exportContacts(q, filters);
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isDeleting: true });
    try {
      await ContactAPI.delete(id);
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isDeleting: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },

  deleteCustomAttributes: async ({ commit }, { id, customAttributes }) => {
    try {
      const response = await ContactAPI.destroyCustomAttributes(
        id,
        customAttributes
      );
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteAvatar: async ({ commit }, id) => {
    try {
      const response = await ContactAPI.destroyAvatar(id);
      commit(types.EDIT_CONTACT, response.data.payload);
    } catch (error) {
      throw new Error(error);
    }
  },

  fetchContactableInbox: async ({ commit }, id) => {
    commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: true });
    try {
      const response = await ContactAPI.getContactableInboxes(id);
      const contact = {
        id,
        contactableInboxes: response.data.payload,
      };
      commit(types.SET_CONTACT_ITEM, contact);
      return contact;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new ExceptionWithMessage(error.response.data.message);
      } else {
        throw new Error(error);
      }
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isFetchingInboxes: false });
    }
  },

  updatePresence: ({ commit }, data) => {
    commit(types.UPDATE_CONTACTS_PRESENCE, data);
  },

  setContact({ commit }, data) {
    commit(types.SET_CONTACT_ITEM, data);
  },

  merge: async ({ commit }, { childId, parentId }) => {
    commit(types.SET_CONTACT_UI_FLAG, { isMerging: true });
    try {
      const response = await AccountActionsAPI.merge(parentId, childId);
      commit(types.SET_CONTACT_ITEM, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_UI_FLAG, { isMerging: false });
    }
  },

  deleteContactThroughConversations: ({ commit }, id) => {
    commit(types.DELETE_CONTACT, id);
    commit(types.CLEAR_CONTACT_CONVERSATIONS, id, { root: true });
  },

  updateContact: async ({ commit }, updateObj) => {
    commit(types.SET_CONTACT_UI_FLAG, { isUpdating: true });
    try {
      commit(types.EDIT_CONTACT, updateObj);
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isUpdating: false });
    }
  },

  // filter: async (
  //   { commit },
  //   { page = 1, sortAttr, queryPayload, resetState = true } = {}
  // ) => {
  //   commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
  //   try {
  //     const {
  //       data: { payload, meta },
  //     } = await ContactAPI.filter(page, sortAttr, queryPayload);
  //     if (resetState) {
  //       commit(types.CLEAR_CONTACTS);
  //       commit(types.SET_CONTACTS, payload);
  //       commit(types.SET_CONTACT_META, meta);
  //       commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
  //     }
  //     return payload;
  //   } catch (error) {
  //     commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
  //   }
  //   return [];
  // },



  filter: async (
    { commit },
    {
      page = 1,
      sortAttr,
      queryPayload,
      search = '',
      actions = [],
      isActions = false,
      isRefetchAfterAction = false,
      resetState = true,
    } = {}
  ) => {
    // Only show loader when starting the initial request. Skip for follow-up refetch after action
    // so we don't trigger a second loader (isFetching is already true from the first request).
    if (!isRefetchAfterAction) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: true });
      // When running filter+action, clear the list immediately so we show a single loading state
      // (loader on empty) instead of loader-on-table then loader-on-empty.
      if (isActions) {
        commit(types.CLEAR_CONTACTS);
        commit(types.SET_CONTACT_META, { count: 0, current_page: 1 });
      }
    }
    try {
      const {
        data: { payload, meta },
      } = await ContactAPI.filter(page, sortAttr, {
        filters: queryPayload,
        actions,
        is_actions: isActions,
      }, search);

      if (resetState) {
        if (isActions) {
          // Filter + action: list already cleared above; keep loader visible until refetch completes.
          commit(types.CLEAR_CONTACTS);
          commit(types.SET_CONTACT_META, { count: 0, current_page: 1 });
          // Do not set isFetching: false — loader stays until the follow-up filter (without actions) completes.
        } else {
          commit(types.CLEAR_CONTACTS);
          commit(types.SET_CONTACTS, payload);
          commit(types.SET_CONTACT_META, meta);
          commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
        }
      }
      return payload;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isFetching: false });
    }
    return [];
  },







  setContactFilters({ commit }, data) {
    commit(types.SET_CONTACT_FILTERS, data);
  },
  setContactActions({commit}, data){
    
    commit(types.SET_CONTACT_ACTIONS, data);

  },

  clearContactFilters({ commit }) {
    commit(types.CLEAR_CONTACT_FILTERS);
  },
  
  dialpadContacts: async ({ commit }) => {
    try {
      const { data: { payload } } = await ContactAPI.dialpadContacts();
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      return payload;
    } catch (error) {
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error(error);
      }
    }
  },
  callHold: ({ commit }, data) => {
    commit(types.CALL_HOLD, data);
  },

  toggleAuthStatusTrue: async ({ commit }) => {
    commit(types.TOGGLE_AUTH_STATUS_TRUE);
  },

  toggleAuthStatusFalse: async ({ commit }) => {
    commit(types.TOGGLE_AUTH_STATUS_FALSE);
  },

  toggleHoldStatusTrue: async ({ commit }) => {
    commit(types.TOGGLE_HOLD_STATUS_TRUE);
  },

  toggleHoldStatusFalse: async ({ commit }) => {
    commit(types.TOGGLE_HOLD_STATUS_FALSE);
  },

  toggleholdCallAvailableStatusTrue: async ({ commit }) => {
    commit(types.TOGGLE_HOLD_CALL_AVAILABLE_STATUS_TRUE);
  },

  toggleholdCallAvailableStatusFalse: async ({ commit }) => {
    commit(types.TOGGLE_HOLD_CALL_AVAILABLE_STATUS_FALSE);
  },
  demo_time: async ({ commit }, { conversationId, data })=>{
    const response=await ContactAPI.update_demo(conversationId,data);
    const {data: responsedata}=response;  
    return responsedata;
  },
    demo_contact_time: async ({ commit }, { contactId, data })=>{
    const response=await ContactAPI.update_contact_demo(contactId,data);
    const {data: responsedata}=response;  
    return responsedata;
  },
  save_demo_meeting: async ({ commit }, { contactId, data })=>{
    const response=await ContactAPI.update_contact_demo_meeting(contactId, data);
    const {data: responsedata}=response;
    // Update the contact in the store with the new data
    if (responsedata.success && responsedata.data) {
      commit(types.EDIT_CONTACT, responsedata.data);
    }
    return responsedata;
  },
  demo_mark_read:async ({ commit }, { contactId, data })=>{
    const response=await ContactAPI.update_contact_demo_read(contactId,data);
    const {data: responsedata}=response;  
    return responsedata;
  },
  set_demo: async ({ commit }, { data })=>{
    const response=await ContactAPI.schedule_demo(data);
    const {data: responsedata}=response;  
    return responsedata;
  },
  getslot: async ({commit},{eventId})=>{
    const response= await ContactAPI.getSlot(eventId);
    console.log("Response",response)
    const {data: responsedata}=response;  
    return responsedata?.data?.duration;
  },
  updateDemoContact: async ({commit},{contactId,data})=>{
    const response=await ContactAPI.update_demo_contact(contactId,data); 
    const {data: responsedata}=response;  
    return responsedata;
  },
  getState: async ({ commit },{id,contact_id}) => {
    try {
      const response=await ContactAPI.getstate(id,contact_id)
      
      console.log("Response",response);
      return response;
      // commit(SET_CURRENT_USER, data);
    } catch (error) {
      // Ignore error
    }
  },
  // assignStatus: async ({ commit },data) => {
  
  //   try {
  //     const {contactId,statusId}=data;
  //     const response = await ContactAPI.assignstatus(contactId,statusId);
  //     return response;
    
  //   } catch (err) {
  //     throw new Error(error);
  //   } finally {
  //     // commit(types.SET_CAMPAIGN_UI_FLAG, { islazyloading: false });
  //   }
  // },
  assignState: async ({ commit },data) => {
    try {
      const {contactId,stateId,description}=data;
      const response = await ContactAPI.assignstate(contactId,stateId,description);
      console.log("Response",response);
      return response;
    } catch (err) {
      throw new Error(error);
    } 
  },
  getBoard: async ({ commit },id) => {
    try {
      const response=await ContactAPI.getboard(id);
      console.log("Response",response);
      return response;
      // commit(SET_CURRENT_USER, data);
    } catch (error) {
      // Ignore error
    }
  },
  assignBoard: async ({ commit },data) => {
    try {
      const {contactId,boardId,description}=data;
      const response = await ContactAPI.assignboard(contactId,boardId,description);
      console.log("Response",response);
      return response;
    } catch (err) {
      throw new Error(error);
    } 
  },
  sendReport: async ({ commit },data) => {
    commit(types.SET_CONTACT_UI_FLAG, { isCreating: true });
     try {
      const response = await ContactAPI.sendreport(data);
      console.log("Response",response);
      return response.data;
    } catch (err) {
      throw new Error(error);
    } 
    finally{
      commit(types.SET_CONTACT_UI_FLAG, { isCreating: false });
    }
  },
};